import { DeliveryTypeCategory } from '@/features/orders/constants/app'
import CustomerLookupInput from '@/features/pos/components/CustomerProfile/parts/CustomerLookupInput'
import DeliveryModeSwitcher from '@/features/pos/components/CustomerProfile/parts/DeliveryModeSwitcher'
import AddressValidationMessage from '@/features/pos/components/CustomerProfile/parts/FormContent/parts/AddressValidationMessage'
import PhoneValidationMessage from '@/features/pos/components/CustomerProfile/parts/FormContent/parts/PhoneValidationMessage'
import Pill from '@/features/ui/components/Pill'
import Typography from '@/features/ui/typography'
import { FC, RefObject } from 'react'
import { useTranslation } from 'react-i18next'
import { StyleSheet, TextInput, View } from 'react-native'

interface FormContentProps {
    addressInputRef: RefObject<TextInput | null>
    addressQuery: string
    compact?: boolean
    deliveryTypeCategories: DeliveryTypeCategory[]
    draftDeliveryTypeCategory: DeliveryTypeCategory
    name: string
    nameInputRef: RefObject<TextInput | null>
    onAddressQueryChange: (value: string) => void
    onDeliveryTypeCategoryChange: (value: DeliveryTypeCategory) => void
    onNameChange: (value: string) => void
    onPhoneChange: (value: string) => void
    phone: string
    phoneInputRef: RefObject<TextInput | null>
    requiredAddress: boolean
}

const RequiredBadge: FC = () => {
    const { t } = useTranslation()

    return <Pill color="warning">{t('form.requiredFieldLabel')}</Pill>
}

const FormContent: FC<FormContentProps> = ({
    addressInputRef,
    addressQuery,
    compact = false,
    deliveryTypeCategories,
    draftDeliveryTypeCategory,
    name,
    nameInputRef,
    onAddressQueryChange,
    onDeliveryTypeCategoryChange,
    onNameChange,
    onPhoneChange,
    phone,
    phoneInputRef,
    requiredAddress,
}) => {
    const { t } = useTranslation()
    const topInputStyle = compact ? styles.compactInput : styles.input
    const hasDeliveryModes = deliveryTypeCategories.length > 0

    return (
        <>
            <Typography.Heading
                style={compact ? styles.compactHeading : styles.heading}
                variant="h2"
            >
                {t('pos.customerLookupModal.heading')}
            </Typography.Heading>
            <View style={compact ? styles.compactTopRow : styles.fieldRow}>
                <View style={compact ? styles.compactTopField : styles.fieldColumn}>
                    <View style={styles.labelRow}>
                        <Typography.Heading style={styles.fieldHeading} variant="h5">
                            {t(
                                compact
                                    ? 'pos.customerProfile.formField.phone.label'
                                    : 'pos.customerLookupModal.fields.search.label'
                            )}
                        </Typography.Heading>
                        <RequiredBadge />
                    </View>
                    <CustomerLookupInput
                        inputRef={phoneInputRef}
                        keyboardType="phone-pad"
                        onChange={onPhoneChange}
                        onSubmitEditing={() => nameInputRef.current?.focus()}
                        placeholder={t(
                            compact
                                ? 'pos.customerProfile.formField.phone.placeholder'
                                : 'pos.customerLookupModal.fields.search.placeholder'
                        )}
                        returnKeyType="next"
                        style={topInputStyle}
                        value={phone}
                    />
                    <PhoneValidationMessage />
                </View>
                <View style={compact ? styles.compactTopField : styles.fieldColumn}>
                    <View style={styles.labelRow}>
                        <Typography.Heading style={styles.fieldHeading} variant="h5">
                            {t('pos.customerLookupModal.fields.name.label')}
                        </Typography.Heading>
                    </View>
                    <CustomerLookupInput
                        inputRef={nameInputRef}
                        onChange={onNameChange}
                        onSubmitEditing={() =>
                            requiredAddress ? addressInputRef.current?.focus() : undefined
                        }
                        placeholder={t('pos.customerLookupModal.fields.name.placeholder')}
                        returnKeyType={requiredAddress ? 'next' : 'done'}
                        style={topInputStyle}
                        value={name}
                    />
                </View>
            </View>
            {hasDeliveryModes ? (
                <View style={compact ? styles.compactAddressSection : styles.addressSection}>
                    <View style={compact ? styles.compactAddressRow : styles.addressRow}>
                        <View style={styles.deliveryModeSwitcherContainer}>
                            <DeliveryModeSwitcher
                                deliveryTypeCategories={deliveryTypeCategories}
                                onChange={onDeliveryTypeCategoryChange}
                                value={draftDeliveryTypeCategory}
                            />
                        </View>
                        <View style={styles.addressFieldContainer}>
                            <View style={[styles.labelRow, styles.addressLabelRow]}>
                                <Typography.Heading style={styles.fieldHeading} variant="h5">
                                    {t('pos.customerLookupModal.fields.address.label')}
                                </Typography.Heading>
                                {requiredAddress ? <RequiredBadge /> : null}
                            </View>
                            <CustomerLookupInput
                                disabled={!requiredAddress}
                                inputRef={addressInputRef}
                                onChange={onAddressQueryChange}
                                placeholder={t(
                                    'pos.customerLookupModal.fields.address.placeholder'
                                )}
                                returnKeyType="done"
                                style={styles.addressInput}
                                value={addressQuery}
                            />
                            {requiredAddress ? <AddressValidationMessage /> : null}
                        </View>
                    </View>
                </View>
            ) : null}
        </>
    )
}

const styles = StyleSheet.create({
    heading: {
        color: '#111827',
        fontSize: 24,
        lineHeight: 32,
        marginBottom: 33,
    },
    compactHeading: {
        color: '#111827',
        fontSize: 20,
        lineHeight: 28,
    },
    fieldRow: {
        flexDirection: 'row',
        gap: 16,
        marginBottom: 24,
    },
    fieldColumn: {
        flex: 1,
    },
    compactTopRow: {
        alignItems: 'flex-start',
        flexDirection: 'row',
        gap: 16,
        width: '100%',
    },
    compactTopField: {
        flexBasis: '48%',
        flexGrow: 1,
        flexShrink: 1,
        gap: 8,
        minWidth: 0,
    },
    labelRow: {
        alignItems: 'center',
        flexDirection: 'row',
        gap: 8,
        marginBottom: 8,
    },
    fieldHeading: {
        color: '#111827',
    },
    input: {
        minWidth: 0,
    },
    compactInput: {
        minWidth: 0,
    },
    addressSection: {
        gap: 8,
    },
    compactAddressSection: {
        gap: 8,
        width: '100%',
    },
    addressRow: {
        alignItems: 'flex-start',
        flexDirection: 'row',
        gap: 16,
    },
    compactAddressRow: {
        alignItems: 'flex-start',
        flexDirection: 'row',
        gap: 16,
        width: '100%',
    },
    deliveryModeSwitcherContainer: {
        paddingTop: 28,
    },
    addressLabelRow: {
        minHeight: 20,
    },
    addressInput: {
        alignSelf: 'stretch',
    },
    addressFieldContainer: {
        flex: 1,
        minWidth: 0,
    },
})

export default FormContent
